/**
 * Color name to hex code mapping utility
 * Maps plain text color names received from GraphQL to their corresponding hex codes
 * Supports different brand color palettes (default and Flying Flowers)
 */
/** Matches a 3 or 6 digit hex color, with or without a leading '#' */
const HEX_COLOR_PATTERN = /^#?[0-9a-f]{6}$|^#?[0-9a-f]{3}$/i;
/**
 * Default color palette (UK/IE brands) - background + design-specified text colour per name
 * Based on Design Sprint 82 Figma: https://www.figma.com/design/YsFmjPuFnDaDAF0rBHSVcg/Design-Sprint-82--18.05.2026-?node-id=53569-97538
 */
const DEFAULT_BRAND_COLORS = {
    purple: { backgroundColor: '#9b00fe', textColor: '#ffffff' },
    'mid-purple': { backgroundColor: '#c366ff', textColor: '#ffffff' },
    'mid purple': { backgroundColor: '#c366ff', textColor: '#ffffff' },
    midpurple: { backgroundColor: '#c366ff', textColor: '#ffffff' },
    lilac: { backgroundColor: '#e9cbff', textColor: '#000000' },
    red: { backgroundColor: '#ff0050', textColor: '#ffffff' },
    'mid-red': { backgroundColor: '#ff98b8', textColor: '#ffffff' },
    'mid red': { backgroundColor: '#ff98b8', textColor: '#ffffff' },
    midred: { backgroundColor: '#ff98b8', textColor: '#ffffff' },
    pink: { backgroundColor: '#ffcbdb', textColor: '#000000' },
    green: { backgroundColor: '#04ff7e', textColor: '#000000' },
    'mid-green': { backgroundColor: '#82ffc0', textColor: '#000000' },
    'mid green': { backgroundColor: '#82ffc0', textColor: '#000000' },
    midgreen: { backgroundColor: '#82ffc0', textColor: '#000000' },
    'light-green': { backgroundColor: '#cbffe4', textColor: '#000000' },
    'light green': { backgroundColor: '#cbffe4', textColor: '#000000' },
    lightgreen: { backgroundColor: '#cbffe4', textColor: '#000000' },
    yellow: { backgroundColor: '#fedc00', textColor: '#000000' },
    'mid-yellow': { backgroundColor: '#fff098', textColor: '#000000' },
    'mid yellow': { backgroundColor: '#fff098', textColor: '#000000' },
    midyellow: { backgroundColor: '#fff098', textColor: '#000000' },
    'light-yellow': { backgroundColor: '#fff7cb', textColor: '#000000' },
    'light yellow': { backgroundColor: '#fff7cb', textColor: '#000000' },
    lightyellow: { backgroundColor: '#fff7cb', textColor: '#000000' },
    orange: { backgroundColor: '#ff842b', textColor: '#000000' },
    'mid-orange': { backgroundColor: '#ff9c55', textColor: '#000000' },
    'mid orange': { backgroundColor: '#ff9c55', textColor: '#000000' },
    midorange: { backgroundColor: '#ff9c55', textColor: '#000000' },
    peach: { backgroundColor: '#fecca7', textColor: '#000000' },
    black: { backgroundColor: '#000000', textColor: '#ffffff' },
    'dark-grey': { backgroundColor: '#4f4f4f', textColor: '#ffffff' },
    'dark grey': { backgroundColor: '#4f4f4f', textColor: '#ffffff' },
    darkgrey: { backgroundColor: '#4f4f4f', textColor: '#ffffff' },
    'mid-grey': { backgroundColor: '#bdbdbd', textColor: '#000000' },
    'mid grey': { backgroundColor: '#bdbdbd', textColor: '#000000' },
    midgrey: { backgroundColor: '#bdbdbd', textColor: '#000000' },
    'light-grey': { backgroundColor: '#f6f6f6', textColor: '#000000' },
    'light grey': { backgroundColor: '#f6f6f6', textColor: '#000000' },
    lightgrey: { backgroundColor: '#f6f6f6', textColor: '#000000' },
    white: { backgroundColor: '#ffffff', textColor: '#000000' },
};
/**
 * Flying Flowers color palette - background + design-specified text colour per name
 * Based on FF Sprint 69 Figma: https://www.figma.com/design/hyeF73xXfLJ55McvPAfloh/FF-Sprint-69--29.06.2026-?node-id=27283-22481
 */
const FLYING_FLOWERS_COLORS = {
    cobalt: { backgroundColor: '#007fa3', textColor: '#ffffff' },
    jade: { backgroundColor: '#048670', textColor: '#ffffff' },
    'wild-exotic': { backgroundColor: '#e01761', textColor: '#ffffff' },
    'wild exotic': { backgroundColor: '#e01761', textColor: '#ffffff' },
    wildexotic: { backgroundColor: '#e01761', textColor: '#ffffff' },
    'lazy-lime': { backgroundColor: '#bdd245', textColor: '#000000' },
    'lazy lime': { backgroundColor: '#bdd245', textColor: '#000000' },
    lazylime: { backgroundColor: '#bdd245', textColor: '#000000' },
    'lazy-lime-300': { backgroundColor: '#dee9a3', textColor: '#000000' },
    'lazy lime 300': { backgroundColor: '#dee9a3', textColor: '#000000' },
    lazylime300: { backgroundColor: '#dee9a3', textColor: '#000000' },
    'plush-blush': { backgroundColor: '#f7c2ce', textColor: '#000000' },
    'plush blush': { backgroundColor: '#f7c2ce', textColor: '#000000' },
    plushblush: { backgroundColor: '#f7c2ce', textColor: '#000000' },
    'pink-blush': { backgroundColor: '#f7c2ce', textColor: '#000000' },
    'pink blush': { backgroundColor: '#f7c2ce', textColor: '#000000' },
    pinkblush: { backgroundColor: '#f7c2ce', textColor: '#000000' },
    'plush-blush-300': { backgroundColor: '#fadbe2', textColor: '#000000' },
    'plush blush 300': { backgroundColor: '#fadbe2', textColor: '#000000' },
    plushblush300: { backgroundColor: '#fadbe2', textColor: '#000000' },
    'pink-blush-300': { backgroundColor: '#fadbe2', textColor: '#000000' },
    'pink blush 300': { backgroundColor: '#fadbe2', textColor: '#000000' },
    pinkblush300: { backgroundColor: '#fadbe2', textColor: '#000000' },
    'sharp-yellow': { backgroundColor: '#fec800', textColor: '#000000' },
    'sharp yellow': { backgroundColor: '#fec800', textColor: '#000000' },
    sharpyellow: { backgroundColor: '#fec800', textColor: '#000000' },
    'sharp-yellow-300': { backgroundColor: '#ffdf6d', textColor: '#000000' },
    'sharp yellow 300': { backgroundColor: '#ffdf6d', textColor: '#000000' },
    sharpyellow300: { backgroundColor: '#ffdf6d', textColor: '#000000' },
    white: { backgroundColor: '#ffffff', textColor: '#000000' },
    'light-grey': { backgroundColor: '#f6f6f6', textColor: '#000000' },
    'light grey': { backgroundColor: '#f6f6f6', textColor: '#000000' },
    lightgrey: { backgroundColor: '#f6f6f6', textColor: '#000000' },
    'dark-grey': { backgroundColor: '#3c3c3b', textColor: '#ffffff' },
    'dark grey': { backgroundColor: '#3c3c3b', textColor: '#ffffff' },
    darkgrey: { backgroundColor: '#3c3c3b', textColor: '#ffffff' },
    black: { backgroundColor: '#000000', textColor: '#ffffff' },
};
/**
 * Look up a colour definition in a palette, tolerating hyphen/space/no-space name variants
 */
const lookupColor = (palette, normalizedName) => { var _a, _b; return (_b = (_a = palette[normalizedName]) !== null && _a !== void 0 ? _a : palette[normalizedName.replace(/-/g, '_')]) !== null && _b !== void 0 ? _b : palette[normalizedName.replace(/\s+/g, '')]; };
/**
 * Get color result (background + text) for a color name based on brand
 * Returns the mapped color with its design-specified text color
 * If color not found, returns default white background with black text
 *
 * @param colorName - Plain text color name (e.g., "Purple", "Cobalt", "Light Grey")
 * @param isFlyingFlowers - Whether to use Flying Flowers palette (default: false)
 * @returns Object with backgroundColor and textColor hex codes
 */
export const getColorHexCode = (colorName, isFlyingFlowers = false) => {
    if (!colorName) {
        return { backgroundColor: '#ffffff', textColor: '#000000' };
    }
    const normalizedName = colorName.toLowerCase().trim();
    const palette = isFlyingFlowers ? FLYING_FLOWERS_COLORS : DEFAULT_BRAND_COLORS;
    const match = lookupColor(palette, normalizedName);
    if (match) {
        return Object.assign({}, match);
    }
    // If color name is already a hex code, use it (no design-specified text colour to look up)
    if (HEX_COLOR_PATTERN.exec(normalizedName)) {
        const hexCode = normalizedName.startsWith('#') ? normalizedName : `#${normalizedName}`;
        return { backgroundColor: hexCode, textColor: '#000000' };
    }
    console.warn(`Color "${colorName}" not found in ${isFlyingFlowers ? 'Flying Flowers' : 'default'} palette. Using white background with black text.`);
    return { backgroundColor: '#ffffff', textColor: '#000000' };
};
/**
 * Get hex code for a color name (deprecated - use getColorHexCode instead)
 * @deprecated Use getColorHexCode() which returns ColorResult with proper text color
 * @param colorName - Plain text color name
 * @param isFlyingFlowers - Whether to use Flying Flowers palette
 * @returns Hex code string or fallback color
 */
export const getColorHex = (colorName, isFlyingFlowers = false) => {
    const result = getColorHexCode(colorName, isFlyingFlowers);
    return result.backgroundColor;
};
/**
 * Validate if a color name exists in the palette
 * @param colorName - Color name to validate
 * @param isFlyingFlowers - Whether to check Flying Flowers palette
 * @returns true if color exists in palette or is a valid hex code
 */
export const isValidColor = (colorName, isFlyingFlowers = false) => {
    if (!colorName)
        return false;
    const normalizedName = colorName.toLowerCase().trim();
    const palette = isFlyingFlowers ? FLYING_FLOWERS_COLORS : DEFAULT_BRAND_COLORS;
    return !!lookupColor(palette, normalizedName) || HEX_COLOR_PATTERN.exec(normalizedName) !== null;
};
/**
 * Get all available colors for a brand
 * @param isFlyingFlowers - Whether to get Flying Flowers colors
 * @returns Object mapping color names to their background/text color definitions
 */
export const getAvailableColors = (isFlyingFlowers = false) => {
    return isFlyingFlowers ? Object.assign({}, FLYING_FLOWERS_COLORS) : Object.assign({}, DEFAULT_BRAND_COLORS);
};
/**
 * Get all available Flying Flowers colors
 * @returns Object mapping FF color names to their background/text color definitions
 */
export const getFFColors = () => {
    return Object.assign({}, FLYING_FLOWERS_COLORS);
};
/**
 * Get all available default brand colors (UK/IE)
 * @returns Object mapping color names to their background/text color definitions
 */
export const getDefaultColors = () => {
    return Object.assign({}, DEFAULT_BRAND_COLORS);
};
